import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { cssVars } from "components/nav-v2/Wrapper"
import cldSrcSet from "components/retailer/cldSrcSet"
import { ClockGlobal, Hours, Minutes, Seconds } from "components/retailer-clock/clock-elements/ClockElements"
import OpeningHours from "components/retailer-clock/OpeningHours"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { motion } from "framer-motion"
import { DateTime } from "luxon"
import { useEffect, useState } from "react"

const GoldStyles = css`
  .fillColor {
    fill: rgb(var(--rolex-gold)); //#7d5609
  }

  .needleColor {
    fill: rgba(125, 86, 9, 1);
  }

  .fillStroke {
    stroke: rgb(var(--rolex-gold)); //#7d5609;
  }
  .ringColorGradient {
    fill: url(#ring-gradient-gold);
  }
  .mainColorGradient {
    fill: url(#gold-gradient);
  }
  .color-gradient-a {
    fill: url(#color-gradient-gold);
  }
  .color-gradient-b {
    fill: url(#color-gradient-gold-2);
  }
  .shape-lines {
    stroke: #dbcaa5;
  }
  #shapePath1 {
    stroke: none;
    fill-rule: nonzero;
    /* fillopacity: 0.85098; should we have this?*/
  }
`
const SlverStyles = css`
  .fillColor {
    fill: #313131;
  }
  .needleColor {
    fill: #313131;
  }

  .fillStroke {
    stroke: #3e3e3e;
  }

  .ringColorGradient {
    fill: url(#ring-gradient-silver);
  }

  .mainColorGradient {
    fill: url(#silver-gradient);
  }
  .color-gradient-a {
    fill: url(#color-gradient-silver);
  }

  .color-gradient-b {
    fill: url(#color-gradient-silver-2);
  }

  .shape-lines {
    stroke: #c5c5c5;
  }
`

const Main = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${fullGrid};
  color: rgb(var(--light-black));
  top: 70px;

  ${getMediaQuery("m")} {
    top: 80px;
  }

  --css-var-height-shim: clamp(5rem, 3.4375rem + 3.9063vw, 6.875rem);
`
const TopBanner = styled.div`
  position: relative;
  display: block;
  height: calc(23vh + var(${cssVars.main.safe}));
  ${getMediaQuery("m")} {
    height: calc(37vh + var(${cssVars.main.safe}));
  }
  background-color: rgb(var(--light-grey));
  z-index: 0;
  grid-column: doc-start / doc-end;
  grid-row: top-row / bottom-row;
  top: 0;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const Clock = styled.div`
  position: relative;
  margin-inline: auto;
  max-width: 120px;
  height: 120px;
  transform: translateY(-50%);
  ${({ theme }) => (theme === "gold" ? GoldStyles : SlverStyles)}
  ${getMediaQuery("m")} {
    margin-inline: auto;
    max-width: 150px;
    height: 150px;
  }
  grid-column: doc-start / doc-end;
  align-items: center;
  justify-items: center;
  grid-row: 3;
  z-index: 1;
`
const CityTimeWrapper = styled.div`
  text-align: center;
  margin-top: -20px;
  ${getMediaQuery("m")} {
    margin-top: -30px;
  }
  ${getMediaQuery("s", { max: true })} {
    margin-top: 0px;
  }
  grid-column: doc-start / doc-end;
  grid-row: 4;
  font-weight: bold;
`

const ClockShadow = styled.div`
  position: absolute;
  width: 115px;
  height: 115px;
  ${getMediaQuery("m")} {
    width: 145px;
    height: 145px;
  }
  box-shadow: 0px 8px 20px -7px rgba(0, 0, 0, 0.5);
  border-radius: 100%;
`
const ClockBG = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  aspect-ratio: 1/1;

  svg {
    width: 100%;
    height: 100%;
  }
`
const HourHand = styled(motion.div)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  /* ransform-origin: 50% 50%; fix this? */
`

const MinuteHand = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  transform-origin: 50% 50%;
  background-color: rgba(0, 255, 0, 0);
`

const SecondHand = styled(motion.div)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  transform-origin: 50% 50%;
  background-color: rgba(255, 0, 0, 0);
`

const Center = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 100%;
  background-color: rgba(2, 74, 2, 0);
`
function getNow(timezone, timeFormat, lang) {
  //console.log("🚀 ~ file: clock.js ~ line 15 ~ getNow ~ timeFormat", timeFormat)
  //const [isH12, setIsH12] = useState(false)

  const date = DateTime.utc()
  //console.log("🚀 ~ file: clock.js ~ line 19 ~ getNow ~ date", date)

  const timezoneDate = DateTime.utc().setZone(timezone)
  //console.log("🚀 ~ file: clock.js ~ line 20 ~ getNow ~ timezoneDate", timezoneDate)

  const hour = timezoneDate.toFormat("H")
  const minute = timezoneDate.toFormat("m")
  const second = timezoneDate.toFormat("s")
  const millisecond = date.millisecond

  const displayYourTime = lang !== "ja" ? date.setLocale(lang).toFormat(timeFormat) : date.toFormat(timeFormat)
  const displayCityTime = lang !== "ja" ? timezoneDate.setLocale(lang).toFormat(timeFormat) : timezoneDate.toFormat(timeFormat)
  return {
    hour,
    minute,
    second,
    millisecond,
    displayYourTime,
    displayCityTime,
  }
}

function useClock(timezone, timeFormat, lang) {
  const [time, setTime] = useState(() => getNow(timezone, timeFormat, lang))

  useEffect(() => {
    const tick = () => setTime(getNow(timezone, timeFormat, lang))
    tick()
    const interval = window.setInterval(tick, 125)

    return () => window.clearInterval(interval)
  }, [timezone, timeFormat, lang])
  return time
}

export function StandardClock({ timezone, timeFormat, ...props }) {
  const timezoneID = timezone ? timezone.timeZoneId : "Europe/Paris"
  const format = timeFormat ? timeFormat : "h:mm a"
  const time = useClock(timezoneID, format)
  const hourAngle = (360 / 12) * time.hour + ((360 / 60) * time.minute) / 12
  const minuteAngle = (360 / 60) * time.minute + ((360 / 60) * time.second) / 60
  const secondAngle = (360 / 60) * time.second + ((360 / 1000) * time.millisecond) / 60

  return (
    <Clock theme='gold' {...props}>
      <ClockBG>
        <ClockGlobal />
        <ClockShadow />
      </ClockBG>
      <Center />
      <HourHand suppressHydrationWarning={true} style={{ transform: `rotate3d(0,0,1,${hourAngle}deg)` }}>
        <Hours />
      </HourHand>
      <MinuteHand suppressHydrationWarning={true} style={{ transform: `rotate3d(0,0,1,${minuteAngle}deg)` }}>
        <Minutes />
      </MinuteHand>
      <SecondHand suppressHydrationWarning={true} style={{ transform: `rotate3d(0,0,1,${secondAngle}deg)` }}>
        <Seconds />
      </SecondHand>
    </Clock>
  )
}
const CityTime = ({ timezone, cityName, format }) => {
  //console.log("🚀 ~ file: clock.js ~ line 86 ~ CityTime ~ format", format.replaceAll(/[<>]/g, "").replace("sup", " a"))
  // const { lang } = useApp()

  const timezoneID = timezone ? timezone.timeZoneId : "Europe/Paris"
  const locale = useLocale()
  const lang = locale.current.codes.ws

  const hourFormat = lang !== "ja" ? "t" : "h:mm a"
  const time = useClock(timezoneID, hourFormat, lang)
  return (
    <CityTimeWrapper>
      <span>{cityName} </span>
      <span suppressHydrationWarning={true}>{time.displayCityTime.toLowerCase()}</span>
    </CityTimeWrapper>
  )
}

export function CoverRetailClock({ type, establishment, StoreClock, ...rest }) {
  const console = useConsole()
  const env = useEnv()
  const { background_image_cld, background_image } = StoreClock
  let {
    cityName,
    openingDetail,
    hoursTranslatedDetails,
    timezone,
    timeFormat,
    storeImages,
    tempOpeningsDateFrom,
    tempOpeningsDateTo,
    tempHoursTranslatedDetails,
  } = establishment

  const now = +Date.now()
  if (tempHoursTranslatedDetails && now >= +new Date(tempOpeningsDateFrom || +"8.64e+15") && now <= +new Date(tempOpeningsDateTo ?? -+"8.64e+15")) {
    hoursTranslatedDetails = tempHoursTranslatedDetails
  }

  console.verbose("🚀 ~ file: CoverRetailClock.js:286 ~ CoverRetailClock ~ hoursTranslatedDetails", hoursTranslatedDetails)

  let storeImg
  try {
    storeImg = JSON.parse(storeImages)
  } catch (error) {
    storeImg = null
  }
  const imgLandscape = Array.isArray(storeImg) ? storeImg.find(item => item.type === "cover-v6") : null
  const imgPortrait = Array.isArray(storeImg) ? storeImg.find(item => item.type === "cover-v6-mobile") : null

  const bannerPicture = {
    landscape: imgLandscape ? imgLandscape?.url : null,
    portrait: imgPortrait ? imgPortrait?.url : null,
  }

  if (bannerPicture.landscape !== null && background_image) background_image.sources[0].src = bannerPicture.landscape
  if (bannerPicture.portrait !== null && background_image) background_image.sources[1].src = bannerPicture.portrait

  console.verbose("CoverRetailClock(%o)", {
    establishment,
    rolexId: establishment.rolexId,
    background_image,
  })

  return (
    <Main>
      <TopBanner>
        <picture>
          <source
            srcSet={cldSrcSet(`/image/upload/d_retailers:a_generic:cover.jpg/q_auto/f_auto/c_limit,w_%w%/v1/retailers/${establishment.rolexId}/cover.jpg`, {
              env,
            })}
            media='(min-width: 768px)'
          />
          <source
            srcSet={cldSrcSet(
              `/image/upload/d_retailers:a_generic:cover-mobile.jpg/q_auto/f_auto/c_limit,w_%w%/v1/retailers/${establishment.rolexId}/cover-mobile.jpg`,
              { env }
            )}
            media='(max-width: 767px)'
          />
          <img alt={background_image_cld?.alt || background_image?.alt} loading='eager' />
        </picture>
      </TopBanner>
      <StandardClock type={type} timezone={timezone} timeFormat={timeFormat} />
      <CityTime cityName={cityName} timezone={timezone} />
      <OpeningHours openingTimes={hoursTranslatedDetails} openingDetail={openingDetail} timezone={timezone} timeFormat={timeFormat} />
    </Main>
  )
}
